import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, Empty, Select } from "./ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScanLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { downloadTransferNote } from "./Transfers";

export default function Distribution() {
  const navigate = useNavigate();
  const [regionals, setRegionals] = useState([]);
  const [binUnits, setBinUnits] = useState([]);
  const [dest, setDest] = useState("");
  const [sel, setSel] = useState([]);
  const [notices, setNotices] = useState([]);
  const [prodFilter, setProdFilter] = useState("");
  const [quickN, setQuickN] = useState(10);
  const [scanText, setScanText] = useState("");
  const [scanMissing, setScanMissing] = useState([]);
  const [scanInput, setScanInput] = useState("");
  const [scanFeed, setScanFeed] = useState([]); // [{serial, status: ok|dup|notfound}]

  const load = useCallback(() => {
    api.get("/locations?type=regional").then((r) => setRegionals(r.data));
    api.get("/units?status=in_bin").then((r) => setBinUnits(r.data));
    api.get("/notices").then((r) => setNotices(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const productOptions = useMemo(() => {
    const seen = {};
    binUnits.forEach((u) => { if (u.product_id) seen[u.product_id] = u.product_name; });
    return Object.entries(seen).map(([value, label]) => ({ value, label }));
  }, [binUnits]);

  const filtered = useMemo(
    () => binUnits.filter((u) => !prodFilter || u.product_id === prodFilter),
    [binUnits, prodFilter]
  );

  const toggle = (s) => setSel((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => sel.includes(u.serial));
  const toggleSelectAll = () => {
    const serials = filtered.map((u) => u.serial);
    setSel((p) => allFilteredSelected ? p.filter((x) => !serials.includes(x)) : Array.from(new Set([...p, ...serials])));
  };

  const quickPick = () => {
    const n = Math.max(0, Number(quickN) || 0);
    const serials = filtered.slice(0, n).map((u) => u.serial);
    if (serials.length === 0) { toast.error("No units match the current filter"); return; }
    setSel(serials);
    if (serials.length < n) toast.info(`Only ${serials.length} unit(s) available — all selected`);
  };

  const applyScan = () => {
    const wanted = Array.from(new Set(scanText.split(/[\n,\s]+/).map((s) => s.trim()).filter(Boolean)));
    if (wanted.length === 0) { toast.error("Paste or scan some serials first"); return; }
    const binSerials = new Set(binUnits.map((u) => u.serial));
    const matched = wanted.filter((s) => binSerials.has(s));
    const missing = wanted.filter((s) => !binSerials.has(s));
    setScanMissing(missing);
    if (matched.length === 0) { toast.error("None of those serials are in the main bin"); setSel([]); return; }
    setSel(matched);
    toast.success(`${matched.length} serial(s) matched & selected${missing.length ? ` · ${missing.length} not in bin` : ""}`);
  };

  const onScanEnter = (e) => {
    if (e.key !== "Enter" && e.key !== "Tab") return;
    e.preventDefault();
    const s = scanInput.trim();
    setScanInput("");
    if (!s) return;
    if (!binUnits.some((u) => u.serial === s)) {
      setScanFeed((f) => [{ serial: s, status: "notfound" }, ...f]);
      return;
    }
    if (sel.includes(s)) {
      setScanFeed((f) => [{ serial: s, status: "dup" }, ...f]);
      return;
    }
    setSel((p) => [...p, s]);
    setScanFeed((f) => [{ serial: s, status: "ok" }, ...f]);
  };

  const scanAdded = scanFeed.filter((x) => x.status === "ok").length;
  const scanNotFound = scanFeed.filter((x) => x.status === "notfound").length;

  const distribute = async () => {
    if (!dest || sel.length === 0) { toast.error("Pick a regional warehouse and at least one unit"); return; }
    try {
      const { data } = await api.post("/distribution", { to_location_id: dest, serials: sel });
      toast.success(`${data.transfer.transfer_no}: ${data.moved.length} unit(s) dispatched · in transit until the regional confirms receipt`, {
        duration: 10000, position: "bottom-right", action: { label: "Transfer note", onClick: () => downloadTransferNote(data.transfer) },
      });
      setSel([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Stock Distribution to Region" subtitle="Transfer a portion of the main bin to a regional warehouse. System-generated mail + report · no AX visibility.">
        <Select testid="dist-dest" value={dest} onChange={setDest} placeholder="Destination regional WH"
          options={regionals.map((r) => ({ value: r.id, label: `${r.name}${r.region ? ` (${r.region})` : ""}` }))} />
        <Button data-testid="dist-view-transfers" variant="outline" onClick={() => navigate("/transfers")} className="rounded-sm border-slate-300 font-head">Transfers in transit</Button>
        <Button data-testid="distribute-btn" onClick={distribute} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Dispatch {sel.length ? `(${sel.length})` : ""}</Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Units in Main Bin" className="lg:col-span-2">
          <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b border-slate-100" data-testid="dist-bulk-tools">
            <div>
              <div className="overline text-slate-500 mb-1">Product</div>
              <Select testid="dist-product-filter" value={prodFilter} onChange={(v) => setProdFilter(v)} placeholder="All products" options={productOptions} />
            </div>
            <div>
              <div className="overline text-slate-500 mb-1">Quick-pick qty</div>
              <div className="flex items-center gap-2">
                <Input data-testid="dist-quick-qty" type="number" min={1} value={quickN} onChange={(e) => setQuickN(e.target.value)} className="rounded-sm w-24" />
                <Button data-testid="dist-quick-pick" onClick={quickPick} variant="outline" className="rounded-sm border-slate-300 font-head">Quick-pick</Button>
              </div>
            </div>
            <Button data-testid="dist-select-all" onClick={toggleSelectAll} variant="outline" className="rounded-sm border-slate-300 font-head" disabled={filtered.length === 0}>
              {allFilteredSelected ? "Deselect all" : "Select all"}
            </Button>
            <Button data-testid="dist-clear" onClick={() => setSel([])} variant="outline" className="rounded-sm border-slate-300 font-head" disabled={sel.length === 0}>Clear</Button>
            <div className="ml-auto text-sm font-head text-[#0B2545]" data-testid="dist-selected-count">
              <span className="text-[#00857C] font-bold">{sel.length}</span> selected · {filtered.length} shown
            </div>
          </div>
          {binUnits.length === 0 ? <Empty>No units in the main bin. Trigger IP1 in the AX Inbox.</Empty> : (
            <table className="w-full text-sm" data-testid="bin-units-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3 w-8"><input type="checkbox" data-testid="dist-header-select-all" checked={allFilteredSelected} onChange={toggleSelectAll} /></th><th className="py-2 pr-3">Serial</th><th className="py-2 pr-3">Product</th><th className="py-2 pr-3">VBU</th></tr></thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3"><input type="checkbox" data-testid={`chk-${u.serial}`} checked={sel.includes(u.serial)} onChange={() => toggle(u.serial)} /></td>
                    <td className="py-2 pr-3 font-label text-[13px]">{u.serial}</td>
                    <td className="py-2 pr-3 font-medium text-[#0B2545]">{u.product_name}</td>
                    <td className="py-2 pr-3 text-slate-500">{u.vbu_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
        <Panel title="Scan / paste serials">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Live scan (hands-free)</div>
          <p className="text-xs text-slate-400 mb-2">Click the box, then scan each unit — every serial is matched and added to the transfer on Enter.</p>
          <Input data-testid="dist-scan-live" value={scanInput} autoFocus
            onChange={(e) => setScanInput(e.target.value)} onKeyDown={onScanEnter}
            placeholder="Scan a serial and it auto-adds…" className="rounded-sm font-label" />
          <div className="flex items-center gap-3 mt-2 text-xs font-head">
            <span className="text-[#00857C] font-bold" data-testid="scan-added">Added: {scanAdded}</span>
            {scanNotFound > 0 && <span className="text-[#b45309] font-bold" data-testid="scan-notfound">Not found: {scanNotFound}</span>}
            {scanFeed.length > 0 && <button data-testid="scan-feed-clear" className="ml-auto text-slate-400 hover:text-slate-600" onClick={() => setScanFeed([])}>Clear feed</button>}
          </div>
          {scanFeed.length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto border border-slate-100 rounded-sm divide-y divide-slate-50" data-testid="scan-feed">
              {scanFeed.map((x, i) => (
                <div key={i} className="flex items-center justify-between px-2 py-1 text-xs">
                  <span className="font-label">{x.serial}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm"
                    style={x.status === "ok" ? { background: "#f0fdfa", color: "#00857C" }
                      : x.status === "dup" ? { background: "#f1f5f9", color: "#475569" }
                        : { background: "#fff7ed", color: "#b45309" }}>
                    {x.status === "ok" ? "Added" : x.status === "dup" ? "Already added" : "Not in bin"}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-slate-100 mt-4 pt-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Or paste a list</div>
            <p className="text-xs text-slate-400 mb-2">Paste serials (comma or newline separated) — matches in the main bin are auto-selected.</p>
            <Textarea data-testid="dist-scan-text" value={scanText} onChange={(e) => setScanText(e.target.value)} rows={4}
              placeholder={"35678901234567\n35678901234568"} className="rounded-sm font-label text-sm" />
            <div className="flex items-center gap-2 mt-2">
              <Button data-testid="dist-scan-apply" onClick={applyScan} className="rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 font-head gap-1.5">
                <ScanLine className="h-4 w-4" /> Match &amp; select
              </Button>
              {scanText && <Button variant="outline" className="rounded-sm border-slate-300 font-head" onClick={() => { setScanText(""); setScanMissing([]); }}>Reset</Button>}
            </div>
            {scanMissing.length > 0 && (
              <div className="mt-3 text-xs" data-testid="dist-scan-missing">
                <div className="font-head font-semibold text-[#b45309]">{scanMissing.length} not in the bin:</div>
                <div className="font-label text-slate-500 break-words mt-1">{scanMissing.join(", ")}</div>
              </div>
            )}
          </div>
        </Panel>
        <Panel title="Transfer Notices">
          {notices.length === 0 ? <Empty>No transfers yet.</Empty> : (
            <div className="space-y-3">
              {notices.map((n) => (
                <div key={n.id} data-testid={`notice-${n.id}`} className="border-l-2 border-[#00857C] pl-3">
                  <div className="text-sm font-head font-semibold text-[#0B2545]">{n.title}</div>
                  <div className="text-xs text-slate-500">{n.body}</div>
                  <div className="text-[10px] text-slate-400 mt-1 font-label">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
